import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listConnections } from '@/api/endpoints';
import type { Connection } from '@/api/types';

/**
 * App-top banner: surfaces any connections that need re-authentication.
 * Reads from the same `/connections` cache used by the connections page —
 * no extra request when both are mounted.
 */
export function CredentialExpiredBanner() {
  const { data } = useQuery<Connection[]>({
    queryKey: ['connections'],
    queryFn: listConnections,
  });
  const expired = data?.filter((c) => c.status === 'credential_expired') ?? [];
  if (expired.length === 0) return null;

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
      <Alert
        severity="warning"
        role="alert"
        action={
          <Button
            component={RouterLink}
            to="/connections"
            color="inherit"
            size="small"
          >
            Reconnect
          </Button>
        }
      >
        <AlertTitle>
          {expired.length === 1
            ? 'One connection needs reauthentication'
            : `${expired.length} connections need reauthentication`}
        </AlertTitle>
        Sync is paused for {expired.map((c) => c.provider_account_label).join(', ')} until you reconnect.
      </Alert>
    </Box>
  );
}
